import React from 'react';

import {
  type AggregationFunction,
  type ComparisonOperator,
  type MetricType,
} from '../types';
import {BooleanMetricConfig} from './BooleanMetricConfig';
import {FloatMetricConfig} from './FloatMetricConfig';
import {SpanErrorRateConfig} from './SpanErrorRateConfig';
import {StatusMetricConfig} from './StatusMetricConfig';

type MetricConfigurationProps = {
  metricType: MetricType;
  threshold: string;
  operator: ComparisonOperator;
  onThresholdChange: (value: string) => void;
  onOperatorChange: (operator: ComparisonOperator) => void;
  hasError: boolean;
  // Status-specific props
  statusMatchValue?: string;
  aggregationFunction?: AggregationFunction;
  onStatusMatchValueChange?: (value: string) => void;
  onAggregationFunctionChange?: (value: AggregationFunction) => void;
  hasStatusError?: boolean;
};

export const MetricConfiguration = ({
  metricType,
  threshold,
  operator,
  onThresholdChange,
  onOperatorChange,
  hasError,
  statusMatchValue,
  aggregationFunction,
  onStatusMatchValueChange,
  onAggregationFunctionChange,
  hasStatusError,
}: MetricConfigurationProps) => {
  if (metricType === 'str') {
    return null;
  }

  switch (metricType) {
    case 'status':
      return (
        <StatusMetricConfig
          statusMatchValue={statusMatchValue || '2'}
          aggregationFunction={aggregationFunction || 'percentage'}
          threshold={threshold || ''}
          operator={operator}
          onStatusMatchValueChange={onStatusMatchValueChange || (() => {})}
          onAggregationFunctionChange={
            onAggregationFunctionChange || (() => {})
          }
          onThresholdChange={onThresholdChange}
          onOperatorChange={onOperatorChange}
          hasStatusError={hasStatusError || false}
          hasThresholdError={hasError}
        />
      );

    case 'span_status':
      return (
        <SpanErrorRateConfig
          aggregationFunction={aggregationFunction || 'percentage'}
          threshold={threshold || ''}
          operator={operator}
          onAggregationFunctionChange={
            onAggregationFunctionChange || (() => {})
          }
          onThresholdChange={onThresholdChange}
          onOperatorChange={onOperatorChange}
          hasThresholdError={hasError}
        />
      );

    case 'bool':
      return (
        <BooleanMetricConfig
          threshold={threshold || '1'}
          onThresholdChange={value => {
            onThresholdChange(value);
            onOperatorChange('EQUAL');
          }}
          hasError={hasError}
        />
      );

    case 'float':
    default:
      return (
        <FloatMetricConfig
          threshold={threshold}
          operator={operator}
          onThresholdChange={onThresholdChange}
          onOperatorChange={onOperatorChange}
          hasError={hasError}
        />
      );
  }
};
